import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";
import StampCardPreview from "./StampCardPreview";
import Toast from "./Toast";

// Instellingen-tab van het winkeldashboard: winkelnaam, stempels-vereist,
// adres/locatie (met geocoding), logo-upload en de stempelkaart-
// voorvertoning. Losgetrokken uit het oorspronkelijke ShopDashboard.js,
// zelfde aanpak als ShopInfo/ShopInsights/ShopCustomers.
const ShopSettings = ({ shopId, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({
    name: "",
    logoUrl: "",
    colorPrimary: "#000000",
    colorSecondary: "#ffffff",
    qrCodeId: shopId,
    stampsRequired: 10,
    plan: "free",
    location: "",
    latitude: null,
    longitude: null
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);

  const showToast = (message, type = "success") => setToast({ message, type });

  useEffect(() => {
    if (!shopId) return;
    const shopRef = doc(db, "shops", shopId);
    getDoc(shopRef).then((docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const locationFromSignup = [data.address, data.postalCode, data.city]
          .filter(Boolean)
          .join(', ');
        setForm({
          ...data,
          qrCodeId: shopId,
          location: data.location || locationFromSignup || '',
          stampsRequired: parseInt(data.stampsRequired) || 10
        });
      } else {
        setForm((f) => ({ ...f, qrCodeId: shopId }));
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'stampsRequired') {
      const numValue = parseInt(value) || 0;
      setForm(prev => ({ ...prev, [name]: numValue }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const geocodeLocation = async (address, { silent = false } = {}) => {
    if (!address || address.trim() === '') {
      if (!silent) showToast("Voer een geldig adres in.", "error");
      return null;
    }

    const mapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!mapsApiKey) {
      if (!silent) {
        showToast("Google Maps API key ontbreekt. Configureer REACT_APP_GOOGLE_MAPS_API_KEY.", "error");
      }
      return null;
    }

    setLocationLoading(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${mapsApiKey}`
      );

      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        const formattedAddress = data.results[0].formatted_address;
        const result = {
          location: formattedAddress,
          latitude: location.lat,
          longitude: location.lng
        };

        setForm(prev => ({ ...prev, ...result }));
        if (!silent) {
          showToast(`Locatie gevonden: ${formattedAddress}`);
        }
        return result;
      }

      if (!silent) {
        showToast("Locatie niet gevonden. Controleer het adres en probeer opnieuw.", "error");
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      if (!silent) {
        showToast("Fout bij het zoeken van de locatie. Probeer het opnieuw.", "error");
      }
      return null;
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSave = async () => {
    if (!shopId) return;
    setSaving(true);
    try {
      const shopRef = doc(db, "shops", shopId);

      let dataToSave = {
        ...form,
        stampsRequired: parseInt(form.stampsRequired) || 10
      };

      if (!dataToSave.location || dataToSave.location.trim() === '') {
        showToast("Vul het winkeladres in. Klanten zien dan de dichtstbijzijnde winkel eerst in de app.", "error");
        setSaving(false);
        return;
      }

      if (!dataToSave.latitude || !dataToSave.longitude) {
        const geocoded = await geocodeLocation(dataToSave.location, { silent: true });
        if (!geocoded) {
          showToast("Bevestig je adres met de Zoek-knop zodat de locatie op de kaart klopt.", "error");
          setSaving(false);
          return;
        }
        dataToSave = { ...dataToSave, ...geocoded };
        setForm(prev => ({ ...prev, ...geocoded }));
      }

      await setDoc(shopRef, dataToSave, { merge: true });
      showToast("Winkel instellingen opgeslagen!");

      setForm(prev => ({ ...prev, stampsRequired: dataToSave.stampsRequired }));
      if (onSaved) onSaved({ name: dataToSave.name, plan: dataToSave.plan });
    } catch (error) {
      showToast("Fout bij opslaan: " + error.message, "error");
    }
    setSaving(false);
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;

    if (logoFile.size > 2 * 1024 * 1024) {
      showToast("Bestand is te groot. Maximum grootte is 2MB.", "error");
      return;
    }

    if (!logoFile.type.startsWith('image/')) {
      showToast("Alleen afbeeldingsbestanden zijn toegestaan.", "error");
      return;
    }

    setLogoUploading(true);
    try {
      const timestamp = Date.now();
      const fileExtension = logoFile.name.split('.').pop();
      const uniqueFileName = `logo_${timestamp}.${fileExtension}`;
      const logoRef = ref(storage, `shops/${shopId}/logo/${uniqueFileName}`);

      const metadata = {
        contentType: logoFile.type,
        cacheControl: 'public, max-age=31536000',
      };

      await uploadBytes(logoRef, logoFile, metadata);

      const downloadURL = await getDownloadURL(logoRef);

      setForm(prev => ({ ...prev, logoUrl: downloadURL }));
      setLogoFile(null);
      setLogoPreview("");
      showToast("Logo succesvol geüpload!");

    } catch (error) {
      console.error('LOGO UPLOAD ERROR:', error);

      let errorMessage = "Fout bij uploaden logo: ";
      if (error.code === 'storage/unauthorized') {
        errorMessage += "Geen toestemming om bestanden te uploaden.";
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage += "Opslaglimiet bereikt.";
      } else if (error.code === 'storage/network-request-failed') {
        errorMessage += "Netwerkfout. Controleer je internetverbinding.";
      } else {
        errorMessage += error.message;
      }

      showToast(errorMessage, "error");
    } finally {
      setLogoUploading(false);
    }
  };

  const uploadLogoAlternative = async () => {
    if (!logoFile) return;

    setLogoUploading(true);
    try {
      const timestamp = Date.now();
      const fileExtension = logoFile.name.split('.').pop();
      const uniqueFileName = `logo_${timestamp}.${fileExtension}`;

      const uploadPaths = [
        `shops/${shopId}/logo/${uniqueFileName}`,
        `logos/${shopId}/${uniqueFileName}`,
        `uploads/logos/${shopId}/${uniqueFileName}`,
        `photos/${shopId}/logo/${uniqueFileName}`
      ];

      let downloadURL = null;
      let uploadError = null;

      for (const uploadPath of uploadPaths) {
        try {
          const logoRef = ref(storage, uploadPath);

          const metadata = {
            contentType: logoFile.type,
            cacheControl: 'public, max-age=31536000',
          };

          await uploadBytes(logoRef, logoFile, metadata);
          downloadURL = await getDownloadURL(logoRef);
          break;
        } catch (error) {
          uploadError = error;
          continue;
        }
      }

      if (downloadURL) {
        setForm(prev => ({ ...prev, logoUrl: downloadURL }));
        setLogoFile(null);
        setLogoPreview("");
        showToast("Logo succesvol geüpload!");
      } else {
        throw uploadError || new Error('Alle upload paden gefaald');
      }

    } catch (error) {
      console.error('LOGO UPLOAD ERROR (alternative):', error);
      showToast("Fout bij uploaden logo: " + error.message, "error");
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!form.logoUrl) return;

    try {
      const url = new URL(form.logoUrl);
      const path = decodeURIComponent(url.pathname.split('/o/')[1]?.split('?')[0] || '');

      if (path) {
        const logoRef = ref(storage, path);
        await deleteObject(logoRef);
      }

      setForm(prev => ({ ...prev, logoUrl: "" }));
      showToast("Logo verwijderd!");

    } catch (error) {
      console.error('LOGO REMOVE ERROR:', error);
      setForm(prev => ({ ...prev, logoUrl: "" }));
      showToast("Logo verwijderd uit instellingen (storage cleanup gefaald)");
    }
  };

  const testStorageConnection = async () => {
    try {
      const testContent = "test";
      const testBlob = new Blob([testContent], { type: 'text/plain' });
      const testRef = ref(storage, `test/${shopId}/connection_test_${Date.now()}.txt`);

      await uploadBytes(testRef, testBlob);
      await deleteObject(testRef);

      showToast("✅ Firebase Storage connectie werkt! Logo upload zou moeten werken.");

    } catch (error) {
      console.error('STORAGE TEST ERROR:', error);
      showToast("❌ Firebase Storage connectie gefaald: " + error.message, "error");
    }
  };

  if (loading) {
    return (
      <div className="card card-flat">
        <div className="card-body text-center">
          <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
          <p>Instellingen laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card card-flat" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>Winkel Instellingen</h3>
        </div>
        <div className="card-body">
          <div className="form-group">
            <label className="form-label">Winkelnaam</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Aantal stempels voor beloning</label>
            <input
              type="number"
              name="stampsRequired"
              value={form.stampsRequired}
              onChange={handleChange}
              className="form-input"
              min="1"
              max="50"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Winkeladres (verplicht)</label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
              Klanten zien in de app de stempelkaart van de winkel het dichtst bij hen bovenaan.
              Zoek je adres op en sla op.
            </p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Voer adres in (bijv. Damrak 1, Amsterdam)"
                  value={form.location}
                  onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                  className="form-input"
                />
              </div>
              <button
                onClick={() => geocodeLocation(form.location)}
                className="btn btn-secondary"
                disabled={locationLoading}
                style={{ whiteSpace: 'nowrap' }}
              >
                {locationLoading ? 'Zoeken...' : '🔍 Zoek'}
              </button>
            </div>
            {(form.latitude && form.longitude) && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                📍 Coördinaten: {form.latitude.toFixed(6)}, {form.longitude.toFixed(6)}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Logo</label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Upload een logo dat wordt gebruikt als stempel in het stempelboekje.
              Ondersteunde formaten: JPG, PNG, GIF. Maximum grootte: 2MB.
            </p>

            {form.logoUrl && (
              <div style={{ marginBottom: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>Huidig logo:</p>
                <img
                  src={form.logoUrl}
                  alt="Huidig logo"
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                />
                <button
                  onClick={removeLogo}
                  className="btn btn-danger"
                  style={{ marginLeft: '10px', marginTop: '10px' }}
                >
                  🗑️ Verwijderen
                </button>
              </div>
            )}

            {logoPreview && (
              <div style={{ marginBottom: '15px', padding: '10px', border: '1px solid #007bff', borderRadius: '8px', backgroundColor: '#f0f8ff' }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>Nieuwe logo preview:</p>
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '2px solid #007bff'
                  }}
                />
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleLogoSelect}
              className="form-input"
              style={{
                padding: '10px',
                border: '2px dashed #ddd',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                cursor: 'pointer'
              }}
            />

            {logoFile && (
              <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={uploadLogo}
                  className="btn btn-primary"
                  disabled={logoUploading}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {logoUploading ? '⏳ Uploaden...' : '📤 Logo uploaden'}
                </button>

                <button
                  onClick={uploadLogoAlternative}
                  className="btn btn-secondary"
                  disabled={logoUploading}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {logoUploading ? '⏳ Uploaden...' : '🔄 Alternatieve upload'}
                </button>
              </div>
            )}

            {logoUploading && (
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #bbdefb' }}>
                <p style={{ margin: '0', color: '#1976d2', fontSize: '14px' }}>
                  🔄 Logo wordt geüpload naar de server...
                </p>
              </div>
            )}

            {process.env.NODE_ENV === 'development' && (
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeaa7' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#856404' }}>🔧 Debug Info:</p>
                <p style={{ margin: '0', fontSize: '11px', color: '#856404' }}>
                  Shop ID: {shopId}<br/>
                  Huidige logo URL: {form.logoUrl || 'Geen'}<br/>
                  Geselecteerd bestand: {logoFile ? `${logoFile.name} (${(logoFile.size / 1024).toFixed(1)}KB)` : 'Geen'}<br/>
                  Upload status: {logoUploading ? 'Bezig' : 'Klaar'}
                </p>
                <button
                  onClick={testStorageConnection}
                  className="btn btn-warning"
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    fontSize: '11px'
                  }}
                >
                  🧪 Test Storage Connectie
                </button>
              </div>
            )}
          </div>

          <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      <div className="card card-flat">
        <div className="card-header">
          <h3>Voorvertoning Stempelkaart</h3>
        </div>
        <div className="card-body">
          <StampCardPreview shopId={shopId} shopName={form.name} />
        </div>
      </div>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ShopSettings;
